import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { AlertCircle, CalendarX, Check, Loader2, MailCheck } from 'lucide-react';
import { lookOf, validHandle } from '@/lib/brandSources';
import { TourChrome, TourMessage } from './TourChrome';
import { Turnstile, turnstileSiteKey } from './Turnstile';
import { bookTour, fetchTourPage, type PageAnswer, type TourPageData } from './tourApi';
import { SlotPicker, groupByDay } from './SlotPicker';
import { TOUR_STRINGS, dayLabel, timeLabel, tourLangOf, type TourLang, type TourStrings } from './strings';
import { adoptLang, useLang } from '@/lib/lang';

/**
 * /o/<handle>/tour: a prospect books a tour of the building, in its brand.
 * One question at a time: home type (optional), day, time, then the details
 * and the notice; the answer is always "check your email" (double opt-in).
 * Shown only for a published, verified organization; anything else is the
 * same "not available" page.
 */
type Load = { kind: 'loading' } | PageAnswer;

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_RE = /^[0-9+\-() ]*$/;

export function TourPage() {
  const handle = validHandle(useParams().handle);
  const [params] = useSearchParams();
  // `?lang=` (an ad, an email) becomes the choice; else the switch's, the browser's, Hebrew.
  const linkLang = tourLangOf(params.get('lang'));
  useEffect(() => adoptLang(linkLang), [linkLang]);
  const lang = useLang((st) => st.lang);
  const s = TOUR_STRINGS[lang];
  const navigate = useNavigate();
  const [load, setLoad] = useState<Load>({ kind: 'loading' });
  const [reload, setReload] = useState(0);
  const [sentTo, setSentTo] = useState<string | null>(null);

  useEffect(() => {
    if (!handle) return setLoad({ kind: 'not-found' });
    let live = true;
    void fetchTourPage(handle, lang).then((a) => {
      if (!live) return;
      if (a.kind === 'redirect')
        navigate(`/o/${a.handle}/tour${lang !== 'he' ? `?lang=${lang}` : ''}`, { replace: true });
      else setLoad(a);
    });
    return () => {
      live = false;
    };
  }, [handle, lang, reload, navigate]);

  const page = load.kind === 'page' ? load.page : null;
  const brand = useMemo(() => (page ? lookOf(page.brand) : null), [page]);

  return (
    <TourChrome brand={brand} loading={load.kind === 'loading'} lang={lang} s={s} title={s.title}>
      {load.kind === 'loading' && (
        <div className="flex justify-center py-10" aria-busy="true">
          <Loader2 className="h-7 w-7 animate-spin text-brand-500" />
        </div>
      )}
      {load.kind === 'not-found' && (
        <TourMessage icon={<AlertCircle className="h-8 w-8 text-slate-400" />} title={s.notFoundTitle}>
          <p className="text-[16px] leading-relaxed text-slate-600">{s.notFoundBody}</p>
        </TourMessage>
      )}
      {load.kind === 'error' && (
        <TourMessage icon={<AlertCircle className="h-8 w-8 text-red-500" />} title={s.loadError} tone="error">
          <button
            type="button"
            onClick={() => {
              setLoad({ kind: 'loading' });
              setReload((n) => n + 1);
            }}
            className="mt-2 min-h-[48px] w-full rounded-xl bg-brand-600 px-4 text-[16px] font-semibold text-white"
          >
            {s.retry}
          </button>
        </TourMessage>
      )}
      {page && sentTo && (
        <TourMessage icon={<MailCheck className="h-9 w-9 text-brand-500" />} title={s.sentTitle}>
          <p className="text-[16px] leading-relaxed text-slate-600">
            {s.sentBody(sentTo, Math.round(page.pendingMinutes / 60))}
          </p>
          <p className="mt-3 text-[16px] leading-relaxed text-slate-500">{s.sentSpam}</p>
        </TourMessage>
      )}
      {page && !sentTo && (
        <BookingForm
          key={`${page.handle}:${lang}`}
          handle={page.handle}
          page={page}
          s={s}
          lang={lang}
          onSent={setSentTo}
          onSlotsStale={() => setReload((n) => n + 1)}
        />
      )}
    </TourChrome>
  );
}

function BookingForm({
  handle,
  page,
  s,
  lang,
  onSent,
  onSlotsStale,
}: {
  handle: string;
  page: TourPageData;
  s: TourStrings;
  lang: TourLang;
  onSent: (email: string) => void;
  onSlotsStale: () => void;
}) {
  const days = useMemo(() => groupByDay(page.slots), [page.slots]);
  const firstFree = days.find((d) => d.anyFree)?.date ?? null;
  const [day, setDay] = useState<string | null>(firstFree);
  const [slot, setSlot] = useState<string | null>(null);
  const [unitTypeId, setUnitTypeId] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [marketing, setMarketing] = useState(false);
  const [website, setWebsite] = useState('');
  const [botToken, setBotToken] = useState<string | null>(null);
  const [botReset, setBotReset] = useState(0);
  const [touched, setTouched] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');

  // A refreshed slot list: keep the choice only while it is still free.
  useEffect(() => {
    if (slot && !page.slots.some((x) => x.start === slot && x.free)) setSlot(null);
    if (day && !days.some((d) => d.date === day)) setDay(firstFree);
  }, [page.slots, days, day, slot, firstFree]);

  const operator = page.brand.displayName;
  const nameErr = touched && name.trim().length < 2 ? s.required : '';
  const emailErr = touched && !EMAIL_RE.test(email.trim()) ? (email.trim() ? s.badEmail : s.required) : '';
  const phoneErr = touched && !PHONE_RE.test(phone) ? s.badPhone : '';
  const needsBot = !!turnstileSiteKey();

  if (!days.some((d) => d.anyFree)) {
    return (
      <>
        <Heading s={s} />
        <TourMessage icon={<CalendarX className="h-8 w-8 text-slate-400" />} title={s.noSlotsTitle}>
          <p className="text-[16px] leading-relaxed text-slate-600">{s.noSlotsBody}</p>
        </TourMessage>
      </>
    );
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    setTouched(true);
    setError('');
    if (!slot || name.trim().length < 2 || !EMAIL_RE.test(email.trim()) || !PHONE_RE.test(phone)) return;
    if (needsBot && !botToken) return setError(s.errors.TOUR_BOT_CHECK!);
    setSending(true);
    const res = await bookTour(handle, {
      slotStart: slot,
      name: name.trim(),
      email: email.trim(),
      ...(phone.trim() ? { phone: phone.trim() } : {}),
      ...(unitTypeId ? { unitTypeId } : {}),
      marketingOptIn: marketing,
      consentVersion: page.consentVersion,
      ...(botToken ? { turnstileToken: botToken } : {}),
      website,
      lang,
    });
    setSending(false);
    if (res.ok) {
      navigator.vibrate?.([30, 50, 30]);
      return onSent(email.trim());
    }
    setError(s.errors[res.code] ?? s.errors.DEFAULT!);
    if (needsBot) setBotReset((n) => n + 1);
    if (res.code === 'TOUR_SLOT_TAKEN' || res.code === 'TOUR_SLOT_UNAVAILABLE' || res.code === 'TOUR_NOTICE_CHANGED') {
      if (res.code !== 'TOUR_NOTICE_CHANGED') setSlot(null);
      onSlotsStale();
    }
  }

  const chosen = slot ? page.slots.find((x) => x.start === slot) : null;

  return (
    <form onSubmit={submit} noValidate className="text-start">
      <Heading s={s} />

      {page.unitTypes.length > 0 && (
        <fieldset className="mb-6 min-w-0">
          <legend className="mb-2 text-[17px] font-bold text-brand-900">{s.unitType}</legend>
          <select
            value={unitTypeId}
            onChange={(e) => setUnitTypeId(e.target.value)}
            className="min-h-[48px] w-full rounded-xl border border-slate-200 bg-white px-3 text-[16px] outline-none focus:border-brand-500"
            aria-label={s.unitType}
          >
            <option value="">{s.unitTypeAny}</option>
            {page.unitTypes.map((u) => (
              <option key={u.id} value={u.id}>
                {u.name}
              </option>
            ))}
          </select>
        </fieldset>
      )}

      {!chosen && (
        <>
          <SlotPicker
            slots={page.slots}
            timeZone={page.timeZone}
            s={s}
            day={day}
            onDay={(d) => {
              setDay(d);
              setSlot(null);
            }}
            onPick={setSlot}
          />
          {error && <ErrorLine text={error} />}
        </>
      )}

      {chosen && (
        <>
          <div className="mb-6 flex items-center justify-between gap-3 rounded-2xl bg-brand-50 p-4">
            <div className="flex items-center gap-3">
              <Check className="h-6 w-6 shrink-0 text-done" aria-hidden />
              <p className="text-[16px] font-semibold text-brand-900">
                {dayLabel(chosen.start, page.timeZone, s)} ·{' '}
                <span dir="ltr">{timeLabel(chosen.start, page.timeZone, s)}</span>
              </p>
            </div>
            <button
              type="button"
              onClick={() => setSlot(null)}
              className="min-h-[48px] shrink-0 rounded-xl px-3 text-[16px] font-semibold text-brand-700"
            >
              {s.changeTime}
            </button>
          </div>

          <h2 className="mb-3 text-[17px] font-bold text-brand-900">{s.yourDetails}</h2>
          <Field id="tour-name" label={s.name} error={nameErr}>
            <input
              id="tour-name"
              autoComplete="name"
              maxLength={120}
              value={name}
              onChange={(e) => setName(e.target.value)}
              aria-invalid={!!nameErr}
              className={INPUT}
            />
          </Field>
          <Field id="tour-email" label={s.email} hint={s.emailHint} error={emailErr}>
            <input
              id="tour-email"
              type="email"
              inputMode="email"
              autoComplete="email"
              dir="ltr"
              maxLength={254}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              aria-invalid={!!emailErr}
              className={INPUT}
            />
          </Field>
          <Field id="tour-phone" label={`${s.phone} (${s.optional})`} error={phoneErr}>
            <input
              id="tour-phone"
              type="tel"
              inputMode="tel"
              autoComplete="tel"
              dir="ltr"
              maxLength={30}
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              aria-invalid={!!phoneErr}
              className={INPUT}
            />
          </Field>

          {/* Honeypot: hidden from people and screen readers; bots fill it. */}
          <div aria-hidden="true" className="absolute -start-[9999px] h-px w-px overflow-hidden">
            <label htmlFor="tour-website">Website</label>
            <input
              id="tour-website"
              name="website"
              tabIndex={-1}
              autoComplete="off"
              value={website}
              onChange={(e) => setWebsite(e.target.value)}
            />
          </div>

          {/* Our notice wording is withdrawn until the operator decides it; their own policy link stays. */}
          {page.privacyPolicyUrl && (
            <a
              href={page.privacyPolicyUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-2 inline-flex min-h-[48px] items-center text-[16px] font-semibold text-brand-700 underline"
            >
              {s.privacyPolicy(operator)}
            </a>
          )}

          <label className="mt-4 flex min-h-[48px] cursor-pointer items-start gap-3 text-[16px] leading-relaxed text-slate-700">
            <input
              type="checkbox"
              checked={marketing}
              onChange={(e) => setMarketing(e.target.checked)}
              className="mt-1 h-6 w-6 shrink-0 accent-brand-600"
            />
            <span>{s.marketing(operator)}</span>
          </label>

          <Turnstile lang={lang === 'en' ? 'en' : 'he'} onToken={setBotToken} resetKey={botReset} />
          {error && <ErrorLine text={error} />}

          <button
            type="submit"
            disabled={sending}
            className="mt-6 flex min-h-[52px] w-full items-center justify-center gap-2 rounded-xl bg-brand-600 px-4 text-[17px] font-semibold text-white disabled:opacity-60"
          >
            {sending && <Loader2 className="h-5 w-5 animate-spin" />}
            {sending ? s.sending : s.submit}
          </button>
        </>
      )}
    </form>
  );
}

const INPUT =
  'min-h-[48px] w-full rounded-xl border border-slate-200 bg-white px-3 text-[16px] outline-none focus:border-brand-500 aria-[invalid=true]:border-red-400';

function Heading({ s }: { s: TourStrings }) {
  return (
    <div className="mb-6">
      <h1 className="text-2xl font-bold text-brand-900">{s.title}</h1>
      <p className="mt-1 text-[16px] leading-relaxed text-slate-600">{s.subtitle}</p>
    </div>
  );
}

function Field({
  id,
  label,
  hint,
  error,
  children,
}: {
  id: string;
  label: string;
  hint?: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="mb-4">
      <label htmlFor={id} className="mb-1.5 block text-[16px] font-semibold text-slate-700">
        {label}
      </label>
      {children}
      {hint && !error && <p className="mt-1 text-[16px] text-slate-500">{hint}</p>}
      {error && <p className="mt-1 text-[16px] text-red-600">{error}</p>}
    </div>
  );
}

function ErrorLine({ text }: { text: string }) {
  return (
    <p
      className="mt-4 flex items-start gap-2 rounded-xl bg-red-50 p-3 text-[16px] leading-relaxed text-red-700"
      role="alert"
    >
      <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" aria-hidden />
      {text}
    </p>
  );
}
